import * as echarts from "echarts";
import type { ChartConfig } from "@bi-studio/shared/lib";
import type { ChartData, ChartSize } from "./types";
import {
  getChartInstance,
  setupResize,
  clearContainer,
  resolveColors,
} from "./utils";

export const renderLineChart = (
  container: HTMLElement | null,
  data: ChartData,
  config: ChartConfig,
  _size?: ChartSize,
  theme: 'light' | 'dark' = 'light'
): HTMLElement | null => {
  if (!container || typeof window === "undefined") return null;

  clearContainer(container);

  // Set container size
  container.style.width = "100%";
  container.style.height = "100%";

  const chartInstance = getChartInstance(container, theme);
  const colors = resolveColors(config, theme);

  // Determine curve type
  let smooth = false;
  let step: false | "start" | "end" | "middle" = false;
  if (config.curve === "basis") {
    smooth = true;
  } else if (config.curve === "step") {
    step = "start";
  }

  // Handle multiple series if seriesField is provided
  if (config.seriesField) {
    // Group data by seriesField
    const grouped: Record<string, Array<{ name: string; value: number }>> = {};
    data.forEach((row) => {
      const seriesKey = String(row[config.seriesField ?? ""] ?? "");
      if (!grouped[seriesKey]) {
        grouped[seriesKey] = [];
      }
      grouped[seriesKey].push({
        name: String(row[config.xField ?? ""] ?? ""),
        value: Number(row[config.yField ?? ""]) || 0,
      });
    });

    // Get unique x values for xAxis
    const xValues = [
      ...new Set(data.map((row) => String(row[config.xField ?? ""] ?? ""))),
    ];

    // Prepare series data
    const series = Object.keys(grouped).map((seriesKey, index) => {
      const seriesData = grouped[seriesKey];
      // Map to xAxis indices
      const mappedData = xValues.map((x) => {
        const item = seriesData.find((d) => d.name === x);
        return item ? item.value : null;
      });

      return {
        name: seriesKey,
        type: "line" as const,
        data: mappedData,
        smooth,
        ...(step && { step }),
        ...(config.stacked && { stack: "Total" }),
        itemStyle: {
          color: colors[index % colors.length],
        },
        lineStyle: {
          width: 3,
        },
        label: {
          show: true,
        },
      };
    });

    const option: echarts.EChartsOption = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: "axis",
      },
      legend: {
        data: Object.keys(grouped),
        top: 10,
      },
      grid: {
        left: "10%",
        right: "10%",
        bottom: "15%",
        top: "20%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xValues,
        boundaryGap: false,
        axisLabel: {
          interval: 0,
          rotate: 45,
        },
      },
      yAxis: {
        type: "value",
        boundaryGap: [0, 0.1],
        show: false,
        axisLabel: {
        },
      },
      series,
    };

    chartInstance.setOption(option);
  } else if (config.yFields && config.yFields.length > 0) {
    // Multiple Y fields (e.g. Stacked Line)
    const xValues = data.map((row) => String(row[config.xField ?? ""] ?? ""));

    const series = config.yFields
      .filter((f) => !!f)
      .map((field, index) => ({
        name: field,
        type: "line" as const,
        data: data.map((row) => Number(row[field]) || 0),
        smooth,
        ...(step && { step }),
        ...(config.stacked && { stack: "Total" }),
        itemStyle: {
          color: colors[index % colors.length],
        },
        lineStyle: {
          width: 3,
        },
        label: {
          show: true,
        },
      }));

    const option: echarts.EChartsOption = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: "axis",
      },
      legend: {
        data: config.yFields,
        top: 10,
      },
      grid: {
        left: "10%",
        right: "10%",
        bottom: "15%",
        top: "20%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xValues,
        boundaryGap: false,
        axisLabel: {
          interval: 0,
          rotate: 45,
        },
      },
      yAxis: {
        type: "value",
        boundaryGap: [0, 0.1],
        show: false,
        axisLabel: {
        },
      },
      series,
    };

    chartInstance.setOption(option);
  } else {
    // Single series
    const xValues = data.map((row) => String(row[config.xField ?? ""] ?? ""));
    const yValues = data.map((row) => Number(row[config.yField ?? ""]) || 0);

    const option: echarts.EChartsOption = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: "axis",
      },
      grid: {
        left: "10%",
        right: "10%",
        bottom: "15%",
        top: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xValues,
        boundaryGap: false,
        axisLabel: {
          interval: 0,
          rotate: 45,
        },
      },
      yAxis: {
        type: "value",
        boundaryGap: [0, 0.1],
        show: false,
        axisLabel: {
        },
      },
      series: [
        {
          name: config.yField || "Value",
          type: "line",
          data: yValues,
          smooth,
          ...(step && { step }),

          itemStyle: {
            color: colors[0],
          },
          lineStyle: {
            width: 3,
            color: colors[0],
          },
          label: {
            show: true,
          },
        },
      ],
    };

    chartInstance.setOption(option);
  }

  setupResize(container, chartInstance);

  return container;
};
